import { FileXls } from '@phosphor-icons/react';
import type { SheetColumnPreview } from '@discovr/types';
import { Button } from '../../../components/ui/button';
import { WizardFooter } from './ImportWizardFooterSlot';
import { compactKey } from './importWizardHelpers';
import { TruncatedText } from '../../../components/common/TruncatedText';

interface ImportUploadReviewStepProps {
  fileName: string;
  rowCount: number;
  sheetColumns: SheetColumnPreview[];
  busy: boolean;
  onContinue: () => void;
  /** Discards the current (unsubmitted) batch and drops back to a fresh
   *  dropzone so the admin can pick a different file entirely. */
  onChangeFile: () => Promise<void>;
}

/** Landing spot for revisiting Upload after Back from Column Mapping — the
 *  sheet is already on the server (still at batch status `mapping`,
 *  unsubmitted), so this isn't a fresh dropzone by default: it shows what
 *  was uploaded (one sample value per column, from the original /preview
 *  response) and lets the admin continue back into mapping OR discard it
 *  and pick a different file via "Change file". No Back of its own — Upload
 *  is the first phase. */
export function ImportUploadReviewStep({ fileName, rowCount, sheetColumns, busy, onContinue, onChangeFile }: ImportUploadReviewStepProps) {
  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center gap-3 rounded-xl border border-border bg-muted/50 px-4 py-3">
        <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-card">
          <FileXls size={16} className="text-foreground" />
        </div>
        <div className="min-w-0 flex-1">
          <TruncatedText as="p" text={fileName} className="text-body-sm-medium text-foreground" />
          <p className="text-caption-regular text-muted-foreground">
            {rowCount} data row{rowCount === 1 ? '' : 's'} · {sheetColumns.length} column{sheetColumns.length === 1 ? '' : 's'}
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" disabled={busy} onClick={() => { onChangeFile().catch(() => {}); }}>
          Change file
        </Button>
      </div>

      {sheetColumns.length > 0 && (
        <div className="overflow-hidden rounded-xl border border-border">
          <table className="w-full border-collapse text-left text-[12px]">
            <thead>
              <tr className="bg-muted">
                <th className="border-b border-border px-3 py-2 font-semibold text-muted-foreground">Sheet column</th>
                <th className="border-b border-border px-3 py-2 font-semibold text-muted-foreground">Sample value</th>
              </tr>
            </thead>
            <tbody>
              {sheetColumns.map((col) => (
                <tr key={col.header} className="border-b border-border last:border-b-0">
                  <td className="px-3 py-2 font-mono text-foreground">{compactKey(col.header)}</td>
                  <td className="px-3 py-2 text-muted-foreground">{col.sampleValue || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <WizardFooter>
        <div className="flex w-full justify-end">
          <Button type="button" variant="default" size="lg" loading={busy} onClick={onContinue}>
            Continue to column mapping
          </Button>
        </div>
      </WizardFooter>
    </div>
  );
}
